// PrintCodLead.js
import React from "react"
import profile from "assets/images/ecs234.jpg"

/**
 * Props:
 *  - id: string|number  (lead id; optional)
 *  - lead: object       (required)
 *  - charges?: object   (optional: from cod_lead_charges)
 */
export default function PrintCodLead({ id, lead, charges, fallbackCharges }) {
  if (!lead) return null
  const effectiveCharges = charges || fallbackCharges
  const fmt = v => (v === null || v === undefined || v === "" ? "—" : String(v))
  const fmtMoney = v => {
    const n = Number(v)
    if (!Number.isFinite(n)) return "—"
    return n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }
  const fmtDate = d => {
    try {
      return new Date(d).toLocaleString()
    } catch {
      return "—"
    }
  }

  return (
    <div className="print-scope">
      <style>{`
        /* Scope everything to avoid global bleed */
        .print-scope { font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif; color:#111827; }
        @media print {
          @page { size: A4; margin: 10mm; }
          .print-scope { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }

        /* Layout */
        .p-wrap { max-width: 800px; margin: 0 auto; }
        .p-head { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
        .p-brand { display:flex; align-items:center; gap:10px; }
        .p-mark { width:28px; height:28px; border-radius:6px; background:#111827; }
        .p-title { font-size:18px; font-weight:800; letter-spacing:.2px; }
        .p-sub { font-size:11px; color:#6b7280; }
        .p-meta { text-align:right; font-size:11px; color:#6b7280; }
        .p-section { margin-top:10px; }
        .p-h2 { font-size:12px; font-weight:700; margin:8px 0 6px; }
        .p-logo{
  width:72px;
  height:72px;
  border-radius:12px;
  background:#ffffff;
  border:1px solid #e5e7eb;
  box-shadow:0 2px 8px rgba(0,0,0,.06);
  display:flex;
  align-items:center;
  justify-content:center;
  overflow:hidden;
}
        .p-logo img{
  width:100%;
  height:100%;
  object-fit:contain;      /* keeps proportions for tall/wide logos */
  padding:6px;             /* breathing room */
}

        /* Tables */
        .p-table { width:100%; border-collapse:collapse; font-size:12px; }
        .p-table th, .p-table td { border:1px solid #e5e7eb; padding:6px 8px; vertical-align:top; }
        .p-table th { background:#f9fafb; font-weight:700; }
        .p-table .right { text-align:right; }
        .p-table .muted { color:#6b7280; }

        /* Two-column blocks side by side */
        .p-grid2 { display:grid; grid-template-columns: 1fr 1fr; gap:10px; }

        /* Totals emphasis */
        .p-total { font-weight:800; }

        /* Keep sections together */
        .avoid-break { page-break-inside: avoid; }

        /* Compact overall to fit one page */
        .compact th, .compact td { padding:5px 7px; }
      `}</style>

      <div className="p-wrap">
        {/* Header */}
        <div className="p-head avoid-break">
          <div className="p-brand">
            <div className="p-logo">
              <img src={profile} alt="" width={100} />
            </div>
            <div>
              <div className="p-title">COD Lead</div>
              <div className="p-sub">
                #{fmt(lead.cod_lead_uni_id)}
                {id ? ` • ID ${id}` : ""}
              </div>
            </div>
          </div>
          <div className="p-meta">
            <div>Printed: {fmtDate(new Date())}</div>
            <div>
              Updated: {fmtDate(lead.lead_updatedAt || lead.lead_createdAt)}
            </div>
          </div>
        </div>

        {/* Business & Consignee (tables, side-by-side) */}
        <div className="p-grid2 avoid-break">
          <div>
            <div className="p-h2">Business</div>
            <table className="p-table compact">
              <tbody>
                <tr>
                  <th style={{ width: 140 }}>Name</th>
                  <td>{fmt(lead.business?.name)}</td>
                </tr>
                <tr>
                  <th>Phone</th>
                  <td>{fmt(lead.business?.phone)}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{fmt(lead.business?.email)}</td>
                </tr>
                <tr>
                  <th>Address</th>
                  <td>{fmt(lead.business?.address)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <div className="p-h2">Consignee</div>
            <table className="p-table compact">
              <tbody>
                <tr>
                  <th style={{ width: 140 }}>Name</th>
                  <td>{fmt(lead.consignee?.name)}</td>
                </tr>
                <tr>
                  <th>Contact(s)</th>
                  <td>
                    {[lead.consignee?.contact1, lead.consignee?.contact2]
                      .filter(Boolean)
                      .join(" / ") || "—"}
                  </td>
                </tr>
                <tr>
                  <th>City</th>
                  <td>{fmt(lead.consignee?.city)}</td>
                </tr>
                <tr>
                  <th>Address</th>
                  <td>{fmt(lead.consignee?.full_address)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Lead & COD (tables, side-by-side) */}
        <div className="p-grid2 p-section avoid-break">
          <div>
            <div className="p-h2">Lead</div>
            <table className="p-table compact">
              <tbody>
                <tr>
                  <th style={{ width: 140 }}>Order ID</th>
                  <td>{fmt(lead.order_id)}</td>
                </tr>
                <tr>
                  <th>Service</th>
                  <td>{fmt(lead.service_type)}</td>
                </tr>
                <tr>
                  <th>Zone</th>
                  <td>{fmt(lead.zone)}</td>
                </tr>
                <tr>
                  <th>Weight</th>
                  <td>{fmt(lead.weight)} kg</td>
                </tr>
                <tr>
                  <th>Quantity</th>
                  <td>{fmt(lead.item_qty)}</td>
                </tr>
                <tr>
                  <th>Origin</th>
                  <td>{fmt(lead.origin)}</td>
                </tr>
                {/* Assigned Rider REMOVED by request */}
              </tbody>
            </table>
          </div>

          <div>
            <div className="p-h2">COD Collection</div>
            <table className="p-table compact">
              <tbody>
                <tr>
                  <th style={{ width: 140 }}>COD Amount</th>
                  <td className="right">{fmtMoney(lead.cod_amount)}</td>
                </tr>
                <tr>
                  <th>Tax Amount</th>
                  <td className="right">{fmtMoney(lead.tax_amount)}</td>
                </tr>
                <tr>
                  <th className="p-total">Total Collect</th>
                  <td className="right p-total">
                    {fmtMoney(lead.total_collect)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Charges */}
        <div className="p-section avoid-break">
          <div className="p-h2">ECS Charges</div>
          <table className="p-table compact">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Item</th>
                <th className="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {effectiveCharges ? (
                <>
                  <tr>
                    <td>Base Weight</td>
                    <td className="right">
                      {fmtMoney(effectiveCharges.base_weight_charge)}
                    </td>
                  </tr>
                  <tr>
                    <td>Cash Handling</td>
                    <td className="right">
                      {fmtMoney(effectiveCharges.cash_handling)}
                    </td>
                  </tr>
                  <tr>
                    <td>FSC</td>
                    <td className="right">
                      {fmtMoney(effectiveCharges.fsc_amount)}
                    </td>
                  </tr>
                  <tr>
                    <td>GST</td>
                    <td className="right">
                      {fmtMoney(effectiveCharges.gst_amount)}
                    </td>
                  </tr>
                  <tr>
                    <td className="p-total">TOTAL</td>
                    <td className="right p-total">
                      {fmtMoney(effectiveCharges.total)}
                    </td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td colSpan={2} className="muted">
                    No priced charges available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {/* zone note under charges (small) */}
          {effectiveCharges?.zone ?? lead.zone ? (
            <div className="p-sub" style={{ marginTop: 4 }}>
              Zone: {fmt(effectiveCharges?.zone ?? lead.zone)}
            </div>
          ) : null}
        </div>

        {/* Signatures (kept minimal) */}
        <div
          className="p-section avoid-break"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 24,
            marginTop: 18,
          }}
        >
          <div>
            <div
              style={{
                borderTop: "1px solid #111827",
                width: 240,
                marginTop: 30,
              }}
            />
            <div className="p-sub" style={{ marginTop: 6 }}>
              Received by (Name / Sign)
            </div>
          </div>
          <div>
            <div
              style={{
                borderTop: "1px solid #111827",
                width: 240,
                marginTop: 30,
              }}
            />
            <div className="p-sub" style={{ marginTop: 6 }}>
              Delivered by
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
